import React, { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Megaphone,
  Plus,
  Pencil,
  Trash2,
  ArrowUp,
  ArrowDown,
  Eye,
  MousePointerClick,
  Percent,
  Clock,
  Sparkles,
  ExternalLink,
  Upload,
  Loader2,
  AlertTriangle,
  Play,
  Pause,
  Sliders,
  Search,
  Users,
  UserCheck,
  Stethoscope,
  Monitor,
  Tablet,
  Smartphone,
  Info,
  ArrowRight,
  ImageIcon,
} from 'lucide-react';
import { Card, Button } from '@medikei/ui';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/Dialog';
import { adminApi, filesApi } from '@/api';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/utils/cn';

export interface BannerItem {
  id: string;
  title: string;
  subtitle?: string;
  badge?: string;
  imageUrl?: string;
  backgroundColor?: string;
  textColor?: string;
  ctaText?: string;
  ctaLink?: string;
  targetAudience: 'ALL' | 'PATIENTS' | 'DOCTORS';
  showOnLandingPage?: boolean;
  order: number;
  slideDurationSeconds: number;
  isActive: boolean;
  startDate?: string | null;
  endDate?: string | null;
  viewsCount: number;
  clicksCount: number;
  createdAt: string;
  updatedAt: string;
}

const GRADIENT_PRESETS = [
  { name: 'Azul Real Medikei', value: 'linear-gradient(135deg, #1677FF 0%, #003EB3 100%)' },
  { name: 'Esmeralda Clínico', value: 'linear-gradient(135deg, #10B981 0%, #047857 100%)' },
  { name: 'Púrpura Especialista', value: 'linear-gradient(135deg, #8B5CF6 0%, #6D28D9 100%)' },
  { name: 'Âmbar Plantão 24h', value: 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)' },
  { name: 'Escuro Noite Minimal', value: 'linear-gradient(135deg, #1E293B 0%, #0F172A 100%)' },
];

export function AdminBannersPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterAudience, setFilterAudience] = useState<'ALL' | 'PATIENTS' | 'DOCTORS'>('ALL');
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'ACTIVE' | 'INACTIVE'>('ALL');

  // Modal de Criação / Edição
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState<BannerItem | null>(null);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [previewDevice, setPreviewDevice] = useState<'desktop' | 'tablet' | 'mobile'>('desktop');

  // Modal de Exclusão
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Modal de Configurações Globais
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [globalSettings, setGlobalSettings] = useState({
    globalAutoplay: true,
    defaultSlideDurationSeconds: 6,
    allowDismissForSession: true,
  });

  // Formulário de Anúncio
  const [form, setForm] = useState({
    title: '',
    subtitle: '',
    badge: 'Novidade',
    imageUrl: '',
    backgroundColor: 'linear-gradient(135deg, #1677FF 0%, #003EB3 100%)',
    textColor: '#FFFFFF',
    ctaText: 'Aproveitar Agora',
    ctaLink: '/expressa',
    targetAudience: 'ALL' as 'ALL' | 'PATIENTS' | 'DOCTORS',
    showOnLandingPage: true,
    slideDurationSeconds: 6,
    isActive: true,
    startDate: '',
    endDate: '',
  });

  // Query dos Anúncios
  const { data: bannersResponse, isLoading } = useQuery({
    queryKey: ['admin', 'banners'],
    queryFn: async () => {
      const res = await adminApi.getBanners();
      return (res as any)?.data !== undefined ? (res as any).data : res;
    },
  });

  const banners: BannerItem[] = useMemo(() => {
    return Array.isArray(bannersResponse?.banners) ? bannersResponse.banners : [];
  }, [bannersResponse]);

  // Sincroniza configurações globais
  React.useEffect(() => {
    if (bannersResponse) {
      setGlobalSettings({
        globalAutoplay: bannersResponse.globalAutoplay ?? true,
        defaultSlideDurationSeconds: bannersResponse.defaultSlideDurationSeconds ?? 6,
        allowDismissForSession: bannersResponse.allowDismissForSession ?? true,
      });
    }
  }, [bannersResponse]);

  // Mutações
  const createBannerMutation = useMutation({
    mutationFn: (data: any) => adminApi.createBanner(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'banners'] });
      setIsModalOpen(false);
      resetForm();
      toast({ title: 'Anúncio criado com sucesso!', variant: 'default' });
    },
    onError: (err: any) => {
      toast({ title: 'Erro ao criar anúncio', description: err?.message, variant: 'error' });
    },
  });

  const updateBannerMutation = useMutation({
    mutationFn: ({ id, ...data }: { id: string } & any) => adminApi.updateBanner(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'banners'] });
      setIsModalOpen(false);
      setEditingBanner(null);
      resetForm();
      toast({ title: 'Anúncio atualizado com sucesso!', variant: 'default' });
    },
    onError: (err: any) => {
      toast({ title: 'Erro ao atualizar anúncio', description: err?.message, variant: 'error' });
    },
  });

  const deleteBannerMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteBanner(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'banners'] });
      setDeleteConfirmId(null);
      toast({ title: 'Anúncio excluído com sucesso!', variant: 'default' });
    },
    onError: (err: any) => {
      toast({ title: 'Erro ao excluir anúncio', description: err?.message, variant: 'error' });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active?: boolean }) => adminApi.toggleBanner(id, active),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'banners'] });
      toast({ title: 'Status do anúncio atualizado!', variant: 'default' });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: (orderList: Array<{ id: string; order: number }>) => adminApi.reorderBanners(orderList),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'banners'] });
      toast({ title: 'Ordem dos anúncios atualizada!', variant: 'default' });
    },
  });

  const updateSettingsMutation = useMutation({
    mutationFn: (data: typeof globalSettings) => adminApi.updateBannerSettings(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'banners'] });
      setIsSettingsModalOpen(false);
      toast({ title: 'Configurações de exibição salvas!', variant: 'default' });
    },
  });

  // Métricas do Motor de Vendas
  const metrics = useMemo(() => {
    const totalActive = banners.filter((b) => b.isActive).length;
    const totalViews = banners.reduce((acc, b) => acc + (b.viewsCount || 0), 0);
    const totalClicks = banners.reduce((acc, b) => acc + (b.clicksCount || 0), 0);
    const overallCtr = totalViews > 0 ? ((totalClicks / totalViews) * 100).toFixed(1) : '0.0';

    return { totalActive, totalViews, totalClicks, overallCtr };
  }, [banners]);

  // Lista Filtrada
  const filteredBanners = useMemo(() => {
    return banners.filter((b) => {
      const matchesSearch =
        b.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (b.subtitle && b.subtitle.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchesAudience =
        filterAudience === 'ALL' || b.targetAudience === filterAudience || b.targetAudience === 'ALL';

      const matchesStatus =
        filterStatus === 'ALL' ||
        (filterStatus === 'ACTIVE' && b.isActive) ||
        (filterStatus === 'INACTIVE' && !b.isActive);

      return matchesSearch && matchesAudience && matchesStatus;
    });
  }, [banners, searchTerm, filterAudience, filterStatus]);

  const resetForm = () => {
    setForm({
      title: '',
      subtitle: '',
      badge: 'Novidade',
      imageUrl: '',
      backgroundColor: 'linear-gradient(135deg, #1677FF 0%, #003EB3 100%)',
      textColor: '#FFFFFF',
      ctaText: 'Aproveitar Agora',
      ctaLink: '/expressa',
      targetAudience: 'ALL',
      showOnLandingPage: true,
      slideDurationSeconds: 6,
      isActive: true,
      startDate: '',
      endDate: '',
    });
  };

  const openCreateModal = () => {
    resetForm();
    setEditingBanner(null);
    setIsModalOpen(true);
  };

  const openEditModal = (banner: BannerItem) => {
    setEditingBanner(banner);
    setForm({
      title: banner.title,
      subtitle: banner.subtitle || '',
      badge: banner.badge || '',
      imageUrl: banner.imageUrl || '',
      backgroundColor: banner.backgroundColor || 'linear-gradient(135deg, #1677FF 0%, #003EB3 100%)',
      textColor: banner.textColor || '#FFFFFF',
      ctaText: banner.ctaText || 'Saiba Mais',
      ctaLink: banner.ctaLink || '/expressa',
      targetAudience: banner.targetAudience || 'ALL',
      showOnLandingPage: banner.showOnLandingPage ?? true,
      slideDurationSeconds: banner.slideDurationSeconds || 6,
      isActive: banner.isActive,
      startDate: banner.startDate ? banner.startDate.slice(0, 16) : '',
      endDate: banner.endDate ? banner.endDate.slice(0, 16) : '',
    });
    setIsModalOpen(true);
  };

  const handleSaveBanner = (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast({ title: 'Preencha o título do anúncio', variant: 'error' });
      return;
    }

    const payload = {
      ...form,
      startDate: form.startDate ? new Date(form.startDate).toISOString() : null,
      endDate: form.endDate ? new Date(form.endDate).toISOString() : null,
      slideDurationSeconds: Number(form.slideDurationSeconds) || 6,
    };

    if (editingBanner) {
      updateBannerMutation.mutate({ id: editingBanner.id, ...payload });
    } else {
      createBannerMutation.mutate(payload);
    }
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsUploadingImage(true);
      const res = await filesApi.uploadFile(file, 'banners', 'DOCTOR' as any, 'COVER_IMAGE', 'PUBLIC');
      const url = res.url || (res.key ? filesApi.getFileUrl(res.key) : '');
      if (url) {
        setForm((prev) => ({ ...prev, imageUrl: url }));
        toast({ title: 'Banner enviado com sucesso!' });
      }
    } catch (err: any) {
      toast({ title: 'Erro ao enviar imagem', description: err?.message, variant: 'error' });
    } finally {
      setIsUploadingImage(false);
    }
  };

  const moveOrder = (index: number, direction: 'up' | 'down') => {
    const targetIdx = direction === 'up' ? index - 1 : index + 1;
    if (targetIdx < 0 || targetIdx >= banners.length) return;

    const copy = [...banners];
    const [moved] = copy.splice(index, 1);
    if (!moved) return;
    copy.splice(targetIdx, 0, moved);

    const reordered = copy.map((item, idx) => ({ id: item.id, order: idx + 1 }));
    reorderMutation.mutate(reordered);
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header com Descrição e Ações */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b border-border pb-5">
        <div className="flex items-center gap-3.5">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center shrink-0 border border-primary/20 shadow-xs">
            <Megaphone className="w-6 h-6" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-foreground tracking-tight flex items-center gap-2">
              <span>Motor de Vendas &amp; Anúncios Globais</span>
            </h1>
            <p className="text-xs sm:text-sm text-muted-foreground mt-0.5 max-w-2xl">
              Crie comunicados, promoções e campanhas que passam no topo das telas de pacientes e médicos. O banner é ocultado automaticamente durante as teleconsultas ao vivo.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2.5 self-start sm:self-auto">
          <Button
            variant="outline"
            onClick={() => setIsSettingsModalOpen(true)}
            className="rounded-xl font-bold text-xs h-10 gap-1.5 cursor-pointer"
          >
            <Sliders className="w-4 h-4 text-primary" />
            <span>Configurações do Slide</span>
          </Button>

          <Button
            onClick={openCreateModal}
            className="rounded-xl font-bold text-xs h-10 px-4 bg-primary hover:bg-primary-hover text-primary-foreground shadow-sm gap-1.5 cursor-pointer"
          >
            <Plus className="w-4 h-4" />
            <span>Novo Anúncio</span>
          </Button>
        </div>
      </div>

      {/* Cards de Métricas do Motor de Vendas */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="rounded-2xl border border-border shadow-2xs bg-card p-4">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">Campanhas Ativas</span>
            <Sparkles className="w-4 h-4 text-emerald-500" />
          </div>
          <p className="text-2xl font-black text-foreground">{metrics.totalActive}</p>
          <span className="text-[11px] text-muted-foreground">de {banners.length} cadastradas</span>
        </Card>

        <Card className="rounded-2xl border border-border shadow-2xs bg-card p-4">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">Visualizações (Views)</span>
            <Eye className="w-4 h-4 text-blue-500" />
          </div>
          <p className="text-2xl font-black text-foreground">{metrics.totalViews.toLocaleString('pt-BR')}</p>
          <span className="text-[11px] text-muted-foreground">Impressões totais no topo</span>
        </Card>

        <Card className="rounded-2xl border border-border shadow-2xs bg-card p-4">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">Cliques (Engajamento)</span>
            <MousePointerClick className="w-4 h-4 text-indigo-500" />
          </div>
          <p className="text-2xl font-black text-foreground">{metrics.totalClicks.toLocaleString('pt-BR')}</p>
          <span className="text-[11px] text-muted-foreground">Acessos gerados para o CTA</span>
        </Card>

        <Card className="rounded-2xl border border-border shadow-2xs bg-card p-4">
          <div className="flex items-center justify-between text-muted-foreground mb-1">
            <span className="text-xs font-semibold uppercase tracking-wider">Taxa de Conversão</span>
            <Percent className="w-4 h-4 text-amber-500" />
          </div>
          <p className="text-2xl font-black text-foreground">{metrics.overallCtr}%</p>
          <span className="text-[11px] text-muted-foreground">Média geral de CTR</span>
        </Card>
      </div>

      {/* Box Oficial de Especificações de Dimensão (Capa YouTube Widescreen) */}
      <div className="rounded-2xl border border-blue-500/30 bg-blue-500/5 dark:bg-blue-950/20 p-4 sm:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4 shadow-xs">
        <div className="flex items-start sm:items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-blue-500/15 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0 border border-blue-500/30">
            <Info className="w-5 h-5" />
          </div>
          <div className="space-y-0.5">
            <h3 className="font-bold text-sm sm:text-base text-foreground flex items-center gap-2">
              <span>Dimensões Oficiais Recomendadas (Capa de YouTube Widescreen)</span>
              <span className="text-[10px] uppercase font-black px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/60 dark:text-blue-200">
                Proporção ~6:1
              </span>
            </h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Para que os anúncios fiquem visualmente cinematográficos e com nitidez máxima em todas as telas, recomendamos exportar suas artes nas seguintes resoluções:
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 shrink-0 text-xs font-semibold">
          <div className="px-3 py-2 rounded-xl bg-card border border-border flex items-center gap-2">
            <Monitor className="w-4 h-4 text-primary shrink-0" />
            <div>
              <span className="text-[10px] text-muted-foreground block font-normal">Computador</span>
              <span className="font-mono font-bold text-foreground">2.560 × 423 px</span>
            </div>
          </div>

          <div className="px-3 py-2 rounded-xl bg-card border border-border flex items-center gap-2">
            <Tablet className="w-4 h-4 text-indigo-500 shrink-0" />
            <div>
              <span className="text-[10px] text-muted-foreground block font-normal">Tablet</span>
              <span className="font-mono font-bold text-foreground">1.855 × 423 px</span>
            </div>
          </div>

          <div className="px-3 py-2 rounded-xl bg-card border border-border flex items-center gap-2">
            <Smartphone className="w-4 h-4 text-emerald-500 shrink-0" />
            <div>
              <span className="text-[10px] text-muted-foreground block font-normal">Celular</span>
              <span className="font-mono font-bold text-foreground">1.546 × 423 px</span>
            </div>
          </div>
        </div>
      </div>

      {/* Barra de Filtros e Busca */}
      <div className="p-4 rounded-2xl bg-card border border-border shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Buscar por título ou descrição da campanha..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full h-10 pl-9 pr-3 text-xs sm:text-sm rounded-xl border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
          />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Filtro Público */}
          <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-xl border border-border">
            <button
              onClick={() => setFilterAudience('ALL')}
              className={cn(
                'px-2.5 py-1 text-xs font-bold rounded-lg transition-all',
                filterAudience === 'ALL' ? 'bg-background text-foreground shadow-2xs' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Todos
            </button>
            <button
              onClick={() => setFilterAudience('PATIENTS')}
              className={cn(
                'px-2.5 py-1 text-xs font-bold rounded-lg transition-all',
                filterAudience === 'PATIENTS' ? 'bg-background text-foreground shadow-2xs' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Pacientes
            </button>
            <button
              onClick={() => setFilterAudience('DOCTORS')}
              className={cn(
                'px-2.5 py-1 text-xs font-bold rounded-lg transition-all',
                filterAudience === 'DOCTORS' ? 'bg-background text-foreground shadow-2xs' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Médicos
            </button>
          </div>

          {/* Filtro Status */}
          <div className="flex items-center gap-1 bg-muted/50 p-1 rounded-xl border border-border">
            <button
              onClick={() => setFilterStatus('ALL')}
              className={cn(
                'px-2.5 py-1 text-xs font-bold rounded-lg transition-all',
                filterStatus === 'ALL' ? 'bg-background text-foreground shadow-2xs' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Todos
            </button>
            <button
              onClick={() => setFilterStatus('ACTIVE')}
              className={cn(
                'px-2.5 py-1 text-xs font-bold rounded-lg transition-all',
                filterStatus === 'ACTIVE' ? 'bg-background text-foreground shadow-2xs' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Ativos
            </button>
            <button
              onClick={() => setFilterStatus('INACTIVE')}
              className={cn(
                'px-2.5 py-1 text-xs font-bold rounded-lg transition-all',
                filterStatus === 'INACTIVE' ? 'bg-background text-foreground shadow-2xs' : 'text-muted-foreground hover:text-foreground'
              )}
            >
              Inativos
            </button>
          </div>
        </div>
      </div>

      {/* Lista de Anúncios */}
      {isLoading ? (
        <div className="py-16 flex flex-col items-center justify-center gap-2 text-muted-foreground">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
          <span className="text-xs">Carregando anúncios do motor de vendas...</span>
        </div>
      ) : filteredBanners.length === 0 ? (
        <Card className="rounded-3xl border border-dashed border-border p-12 text-center bg-muted/10 space-y-4">
          <div className="w-14 h-14 rounded-2xl bg-muted text-muted-foreground flex items-center justify-center mx-auto">
            <Megaphone className="w-7 h-7 opacity-60" />
          </div>
          <div className="max-w-md mx-auto space-y-1">
            <h3 className="font-bold text-base text-foreground">Nenhum anúncio cadastrado ou encontrado</h3>
            <p className="text-xs text-muted-foreground leading-relaxed">
              Crie sua primeira campanha para alavancar consultas expressas, incentivar cadastros de novos médicos ou promover serviços especiais na plataforma.
            </p>
          </div>
          <Button onClick={openCreateModal} className="rounded-xl font-bold text-xs h-10 px-5 gap-1.5 cursor-pointer">
            <Plus className="w-4 h-4" />
            <span>Criar Primeiro Anúncio</span>
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredBanners.map((banner, index) => {
            const ctr = banner.viewsCount > 0 ? ((banner.clicksCount / banner.viewsCount) * 100).toFixed(1) : '0.0';

            return (
              <Card
                key={banner.id}
                className={`rounded-3xl border transition-all duration-300 overflow-hidden shadow-xs hover:shadow-lg ${
                  banner.isActive ? 'border-border bg-card' : 'border-border/60 bg-muted/20 opacity-75'
                }`}
              >
                {/* 1. SHOWCASE WIDESCREEN CINEMATOGRÁFICO DO BANNER (Capa YouTube ~6:1) */}
                <div
                  className="relative w-full overflow-hidden p-5 sm:p-7 flex flex-col sm:flex-row sm:items-center justify-between gap-4 select-none min-h-[140px] sm:min-h-[160px]"
                  style={{
                    background: banner.backgroundColor || 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0284c7 100%)',
                    color: banner.textColor || '#FFFFFF',
                  }}
                >
                  {/* Imagem de Fundo em Widescreen Cover Totalmente Nítida */}
                  {banner.imageUrl && (
                    <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                      <img
                        src={banner.imageUrl}
                        alt={banner.title}
                        className="w-full h-full object-cover object-center"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/20 to-transparent sm:max-w-2xl" />
                    </div>
                  )}

                  {!banner.imageUrl && (
                    <div className="absolute inset-0 z-0 pointer-events-none opacity-20">
                      <div className="absolute -top-16 -left-16 w-64 h-64 rounded-full bg-white/20 blur-2xl" />
                      <div className="absolute -bottom-16 -right-16 w-64 h-64 rounded-full bg-primary/30 blur-2xl" />
                    </div>
                  )}

                  {/* Conteúdo sobreposto: Badge, Título e Subtítulo */}
                  <div className="relative z-10 flex-1 min-w-0 space-y-1.5 text-left">
                    <div className="flex flex-wrap items-center gap-2">
                      {banner.badge && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-0.5 rounded-full text-[10px] sm:text-xs font-black uppercase tracking-wider bg-gradient-to-r from-amber-400/30 to-amber-500/20 text-amber-200 border border-amber-400/40 shadow-xs backdrop-blur-md">
                          <Sparkles className="w-3 h-3 text-amber-300" />
                          <span>{banner.badge}</span>
                        </span>
                      )}
                      <span className="text-[10px] font-bold text-white/60 bg-white/10 px-2 py-0.5 rounded-full backdrop-blur-xs font-mono">
                        Ordem #{banner.order}
                      </span>
                    </div>

                    <h3 className="text-base sm:text-xl lg:text-2xl font-black tracking-tight leading-tight text-white drop-shadow-md truncate">
                      {banner.title}
                    </h3>

                    {banner.subtitle && (
                      <p className="text-xs sm:text-sm text-white/90 line-clamp-1 max-w-2xl font-medium drop-shadow-xs">
                        {banner.subtitle}
                      </p>
                    )}
                  </div>

                  {/* CTA em Destaque */}
                  <div className="relative z-10 shrink-0 flex items-center gap-2">
                    {banner.ctaText && (
                      <span className="px-5 py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-black text-xs rounded-xl shadow-lg flex items-center gap-1.5 border border-emerald-400/30">
                        <span>{banner.ctaText}</span>
                        <ArrowRight className="w-3.5 h-3.5" />
                      </span>
                    )}
                  </div>
                </div>

                {/* 2. BARRA INFERIOR DE CONTROLES, MÉTRICAS E REORDENAÇÃO */}
                <div className="px-5 py-3.5 bg-muted/20 border-t border-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  {/* Ordem e Informações de Exibição */}
                  <div className="flex flex-wrap items-center gap-2.5">
                    {/* Botões de Subir / Descer Ordem */}
                    <div className="flex items-center gap-1 bg-background p-1 rounded-xl border border-border shadow-2xs">
                      <button
                        type="button"
                        disabled={index === 0}
                        onClick={() => moveOrder(index, 'up')}
                        className="p-1 hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:pointer-events-none rounded-lg transition-colors cursor-pointer"
                        title="Mover para cima no carrossel"
                      >
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <span className="text-xs font-black text-foreground px-1.5 font-mono">#{banner.order}</span>
                      <button
                        type="button"
                        disabled={index === filteredBanners.length - 1}
                        onClick={() => moveOrder(index, 'down')}
                        className="p-1 hover:bg-muted text-muted-foreground hover:text-foreground disabled:opacity-30 disabled:pointer-events-none rounded-lg transition-colors cursor-pointer"
                        title="Mover para baixo no carrossel"
                      >
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                    </div>

                    {/* Badge de Status */}
                    <span
                      className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
                        banner.isActive
                          ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20'
                          : 'bg-muted text-muted-foreground border-border'
                      }`}
                    >
                      {banner.isActive ? '● Ativo' : '○ Pausado'}
                    </span>

                    {/* Público */}
                    <span className="px-2.5 py-1 rounded-full text-[11px] font-semibold bg-blue-500/10 text-blue-700 dark:text-blue-300 border border-blue-500/20 flex items-center gap-1">
                      {banner.targetAudience === 'ALL' && <Users className="w-3 h-3" />}
                      {banner.targetAudience === 'PATIENTS' && <UserCheck className="w-3 h-3" />}
                      {banner.targetAudience === 'DOCTORS' && <Stethoscope className="w-3 h-3" />}
                      <span>
                        {banner.targetAudience === 'ALL'
                          ? 'Todos (Pacientes e Médicos)'
                          : banner.targetAudience === 'PATIENTS'
                          ? 'Apenas Pacientes'
                          : 'Apenas Médicos'}
                      </span>
                    </span>

                    <span className="px-2.5 py-1 rounded-full text-[11px] font-medium bg-muted text-muted-foreground flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      <span>{banner.slideDurationSeconds}s no slide</span>
                    </span>

                    {banner.showOnLandingPage !== false && (
                      <span className="px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-purple-500/10 text-purple-700 dark:text-purple-300 border border-purple-500/20">
                        Landing Page Ativa
                      </span>
                    )}

                    {banner.ctaLink && (
                      <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                        <ExternalLink className="w-3 h-3 text-primary" />
                        <code className="bg-muted px-1.5 py-0.5 rounded font-mono text-[10px]">{banner.ctaLink}</code>
                      </span>
                    )}
                  </div>

                  {/* Métricas e Botões de Gestão */}
                  <div className="flex items-center justify-between sm:justify-end gap-5 border-t sm:border-t-0 pt-2.5 sm:pt-0">
                    {/* Performance */}
                    <div className="flex items-center gap-3.5 text-xs">
                      <div>
                        <span className="text-[10px] text-muted-foreground uppercase block font-semibold">Views</span>
                        <span className="font-bold text-foreground">{banner.viewsCount.toLocaleString('pt-BR')}</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-muted-foreground uppercase block font-semibold">Cliques</span>
                        <span className="font-bold text-foreground">{banner.clicksCount.toLocaleString('pt-BR')}</span>
                      </div>
                      <div>
                        <span className="text-[10px] text-muted-foreground uppercase block font-semibold">CTR</span>
                        <span className="font-bold text-emerald-600 dark:text-emerald-400">{ctr}%</span>
                      </div>
                    </div>

                    {/* Botões */}
                    <div className="flex items-center gap-1 shrink-0">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleActiveMutation.mutate({ id: banner.id, active: !banner.isActive })}
                        className="h-9 px-2.5 text-xs font-semibold rounded-xl"
                        title={banner.isActive ? 'Pausar anúncio' : 'Ativar anúncio'}
                      >
                        {banner.isActive ? (
                          <Pause className="w-4 h-4 text-amber-600" />
                        ) : (
                          <Play className="w-4 h-4 text-emerald-600" />
                        )}
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => openEditModal(banner)}
                        className="h-9 px-2.5 text-xs font-semibold rounded-xl text-muted-foreground hover:text-primary hover:bg-primary/10"
                        title="Editar anúncio"
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>

                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setDeleteConfirmId(banner.id)}
                        className="h-9 px-2.5 text-xs font-semibold rounded-xl text-muted-foreground hover:text-destructive hover:bg-destructive/10"
                        title="Excluir anúncio"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* ========================================================= */}
      {/* MODAL DE CRIAÇÃO E EDIÇÃO DE ANÚNCIO (COMPLETA COM PREVIEW)*/}
      {/* ========================================================= */}
      <Dialog open={isModalOpen} onOpenChange={(open) => !open && setIsModalOpen(false)}>
        <DialogContent className="max-w-3xl w-full p-0 overflow-hidden rounded-3xl bg-card border border-border shadow-2xl flex flex-col max-h-[92vh]">
          <DialogHeader className="px-6 py-4 border-b border-border bg-muted/20">
            <DialogTitle className="text-lg font-bold text-foreground flex items-center gap-2">
              <Megaphone className="w-5 h-5 text-primary" />
              <span>{editingBanner ? 'Editar Anúncio da Plataforma' : 'Criar Novo Anúncio da Plataforma'}</span>
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Configure os textos, público-alvo, imagem do banner e link de ação direta.
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSaveBanner} className="p-6 overflow-y-auto space-y-5 text-xs flex-1">
            {/* Live Preview no Topo do Formulário com Seletor de Telas (Widescreen Capa YouTube) */}
            <div className="space-y-2 p-4 rounded-3xl bg-muted/30 border border-border">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-1 border-b border-border/60">
                <div className="flex items-center gap-1.5 font-bold text-xs text-foreground">
                  <Sparkles className="w-3.5 h-3.5 text-primary" />
                  <span>Pré-Visualização ao Vivo da Capa Widescreen:</span>
                </div>

                {/* Seletor de Dispositivo (Desktop / Tablet / Smartphone) */}
                <div className="flex items-center gap-1 bg-background p-1 rounded-xl border border-border self-start sm:self-auto shadow-2xs">
                  <button
                    type="button"
                    onClick={() => setPreviewDevice('desktop')}
                    className={cn(
                      'px-2.5 py-1 rounded-lg font-bold text-[11px] transition-all flex items-center gap-1 cursor-pointer',
                      previewDevice === 'desktop' ? 'bg-primary text-white shadow-xs' : 'text-muted-foreground hover:text-foreground'
                    )}
                    title="Visualizar em Desktop (2.560 × 423 px)"
                  >
                    <Monitor className="w-3 h-3" />
                    <span>Desktop (2.560 px)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPreviewDevice('tablet')}
                    className={cn(
                      'px-2.5 py-1 rounded-lg font-bold text-[11px] transition-all flex items-center gap-1 cursor-pointer',
                      previewDevice === 'tablet' ? 'bg-primary text-white shadow-xs' : 'text-muted-foreground hover:text-foreground'
                    )}
                    title="Visualizar em Tablet (1.855 × 423 px)"
                  >
                    <Tablet className="w-3 h-3" />
                    <span>Tablet (1.855 px)</span>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPreviewDevice('mobile')}
                    className={cn(
                      'px-2.5 py-1 rounded-lg font-bold text-[11px] transition-all flex items-center gap-1 cursor-pointer',
                      previewDevice === 'mobile' ? 'bg-primary text-white shadow-xs' : 'text-muted-foreground hover:text-foreground'
                    )}
                    title="Visualizar em Smartphone (1.546 × 423 px)"
                  >
                    <Smartphone className="w-3 h-3" />
                    <span>Celular (1.546 px)</span>
                  </button>
                </div>
              </div>

              {/* Moldura de Pré-visualização Cinematográfica */}
              <div className="w-full flex justify-center py-2 overflow-hidden">
                <div
                  className={cn(
                    'relative rounded-2xl overflow-hidden shadow-xl border border-white/20 select-none transition-all duration-300 flex flex-col justify-between p-4 sm:p-6',
                    previewDevice === 'desktop' && 'w-full min-h-[160px] sm:min-h-[180px]',
                    previewDevice === 'tablet' && 'w-full max-w-[620px] min-h-[150px]',
                    previewDevice === 'mobile' && 'w-full max-w-[390px] min-h-[140px]'
                  )}
                  style={{
                    background: form.backgroundColor || 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0284c7 100%)',
                    color: form.textColor || '#FFFFFF',
                  }}
                >
                  {/* Imagem em Cover Totalmente Nítida sem Ofuscar */}
                  {form.imageUrl && (
                    <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none">
                      <img
                        src={form.imageUrl}
                        alt="Preview"
                        className="w-full h-full object-cover object-center"
                      />
                      <div className="absolute inset-0 bg-gradient-to-r from-black/55 via-black/20 to-transparent sm:max-w-2xl" />
                    </div>
                  )}

                  <div className="relative z-10 space-y-1.5 text-left flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-1.5">
                      {form.badge && (
                        <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider bg-gradient-to-r from-amber-400/30 to-amber-500/20 text-amber-200 border border-amber-400/40 shadow-xs backdrop-blur-md">
                          <Sparkles className="w-3 h-3 text-amber-300" />
                          <span>{form.badge}</span>
                        </span>
                      )}
                      <span className="text-[10px] font-mono text-white/60 bg-white/10 px-2 py-0.5 rounded-full">
                        {previewDevice === 'desktop' ? '2.560 × 423 px' : previewDevice === 'tablet' ? '1.855 × 423 px' : '1.546 × 423 px'}
                      </span>
                    </div>

                    <h4 className="font-black text-base sm:text-xl lg:text-2xl text-white drop-shadow-md leading-tight truncate">
                      {form.title || 'Título do seu anúncio aqui'}
                    </h4>

                    {form.subtitle && (
                      <p className="text-xs sm:text-sm text-white/90 line-clamp-1 max-w-xl font-medium drop-shadow-xs">
                        {form.subtitle}
                      </p>
                    )}
                  </div>

                  <div className="relative z-10 pt-3 flex items-center justify-between gap-3 border-t border-white/10">
                    <span className="text-[10px] text-white/60 font-mono truncate">
                      Link: {form.ctaLink || '/expressa'}
                    </span>
                    <span className="px-4 py-2 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-black text-xs rounded-xl shadow-md shrink-0 flex items-center gap-1 border border-emerald-400/30">
                      <span>{form.ctaText || 'Saiba Mais'}</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Campos Principais */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="font-bold text-foreground">Título Principal da Campanha *</label>
                <input
                  type="text"
                  required
                  placeholder="Ex: Consulta Expressa com 20% OFF no Plantão"
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div className="space-y-1">
                <label className="font-bold text-foreground">Badge / Selo Promocional</label>
                <input
                  type="text"
                  placeholder="Ex: Oferta Especial, Novo, Destaque"
                  value={form.badge}
                  onChange={(e) => setForm({ ...form, badge: e.target.value })}
                  className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="font-bold text-foreground">Subtítulo / Descrição Curta (Opcional)</label>
              <input
                type="text"
                placeholder="Ex: Fale com um clínico geral agora mesmo sem agendamento prévio."
                value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
            </div>

            {/* Imagem do Banner & Upload com Guia de Dimensões Widescreen */}
            <div className="space-y-2 p-4 rounded-2xl bg-muted/30 border border-border">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1">
                <label className="font-bold text-foreground flex items-center gap-1.5">
                  <ImageIcon className="w-4 h-4 text-primary" />
                  <span>Capa Widescreen do Anúncio (Arte Gráfica)</span>
                </label>
                {isUploadingImage && (
                  <span className="text-primary flex items-center gap-1 text-xs font-semibold">
                    <Loader2 className="w-3.5 h-3.5 animate-spin" /> Enviando imagem...
                  </span>
                )}
              </div>

              <div className="flex flex-col sm:flex-row items-center gap-2">
                <input
                  type="text"
                  placeholder="https://sua-imagem.com/capa-2560x423.jpg ou envie o arquivo..."
                  value={form.imageUrl}
                  onChange={(e) => setForm({ ...form, imageUrl: e.target.value })}
                  className="flex-1 h-10 px-3 rounded-xl border border-input bg-background text-xs font-mono text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary w-full"
                />

                <label className="w-full sm:w-auto h-10 px-4 bg-muted hover:bg-muted/80 text-foreground font-semibold rounded-xl text-xs flex items-center justify-center gap-1.5 cursor-pointer border border-border shrink-0 transition-colors">
                  <Upload className="w-3.5 h-3.5" />
                  <span>Escolher Imagem</span>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileUpload}
                    className="hidden"
                    disabled={isUploadingImage}
                  />
                </label>
              </div>

              {/* Dica de Resolução */}
              <div className="flex items-start gap-2 pt-1 text-[11px] text-muted-foreground leading-relaxed">
                <Info className="w-3.5 h-3.5 text-primary shrink-0 mt-0.5" />
                <span>
                  <strong>Dimensões recomendadas (Capa YouTube ~6:1):</strong> Desktop (<strong>2.560 × 423 px</strong>), Tablet (<strong>1.855 × 423 px</strong>), Celular (<strong>1.546 × 423 px</strong>). A imagem preencherá toda a largura como background cover widescreen de forma cinematográfica.
                </span>
              </div>
            </div>

            {/* Público-Alvo e Duração */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="font-bold text-foreground">Público-Alvo da Exibição *</label>
                <select
                  value={form.targetAudience}
                  onChange={(e) => setForm({ ...form, targetAudience: e.target.value as any })}
                  className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                >
                  <option value="ALL">👥 Todos os Usuários (Pacientes e Médicos)</option>
                  <option value="PATIENTS">🩺 Apenas Pacientes</option>
                  <option value="DOCTORS">👨‍⚕️ Apenas Médicos</option>
                </select>
                <p className="text-[11px] text-muted-foreground">O banner só será entregue para as contas do perfil escolhido.</p>
              </div>

              <div className="space-y-1">
                <label className="font-bold text-foreground">Tempo no Slide (Segundos) *</label>
                <input
                  type="number"
                  min="3"
                  max="60"
                  required
                  value={form.slideDurationSeconds}
                  onChange={(e) => setForm({ ...form, slideDurationSeconds: parseInt(e.target.value, 10) || 6 })}
                  className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm font-bold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
                <p className="text-[11px] text-muted-foreground">Tempo que o anúncio fica visível antes de passar para o próximo.</p>
              </div>
            </div>

            {/* Configuração de Exibição na Landing Page Pública */}
            <div className="p-3.5 rounded-2xl border border-border bg-muted/20 flex items-center justify-between">
              <div>
                <span className="font-bold text-foreground text-xs block">Exibir na Landing Page Principal e na Busca Pública?</span>
                <span className="text-[11px] text-muted-foreground">Se ativado, o anúncio aparecerá no carrossel da página inicial (entre a Hero e os Especialistas) e na tela /buscar.</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer shrink-0 ml-3">
                <input
                  type="checkbox"
                  checked={form.showOnLandingPage ?? true}
                  onChange={(e) => setForm({ ...form, showOnLandingPage: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-muted peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
              </label>
            </div>

            {/* CTA e Link de Destino */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="font-bold text-foreground">Texto do Botão de Ação (CTA)</label>
                <input
                  type="text"
                  placeholder="Ex: Agendar Consulta, Saber Mais"
                  value={form.ctaText}
                  onChange={(e) => setForm({ ...form, ctaText: e.target.value })}
                  className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div className="space-y-1">
                <label className="font-bold text-foreground">Link de Destino ao Clicar *</label>
                <input
                  type="text"
                  required
                  placeholder="Ex: /expressa ou /agendamentos ou https://..."
                  value={form.ctaLink}
                  onChange={(e) => setForm({ ...form, ctaLink: e.target.value })}
                  className="w-full h-10 px-3 rounded-xl border border-input bg-background text-sm font-mono text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            </div>

            {/* Presets de Cor de Fundo */}
            <div className="space-y-1.5">
              <label className="font-bold text-foreground block">Cores e Gradientes Rápidos de Fundo:</label>
              <div className="flex flex-wrap gap-2">
                {GRADIENT_PRESETS.map((p, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={() => setForm({ ...form, backgroundColor: p.value })}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold cursor-pointer transition-all hover:scale-102"
                    style={{ background: p.value, color: '#fff' }}
                  >
                    <span>{p.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Período da Campanha */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
              <div className="space-y-1">
                <label className="font-semibold text-muted-foreground">Data de Início (Opcional)</label>
                <input
                  type="datetime-local"
                  value={form.startDate}
                  onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                  className="w-full h-10 px-3 rounded-xl border border-input bg-background text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-muted-foreground">Data de Término (Opcional)</label>
                <input
                  type="datetime-local"
                  value={form.endDate}
                  onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                  className="w-full h-10 px-3 rounded-xl border border-input bg-background text-xs text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                />
              </div>
            </div>

            {/* Ativo / Inativo */}
            <div className="pt-2 flex items-center justify-between border-t border-border">
              <div>
                <span className="font-bold text-foreground text-sm block">Status da Campanha</span>
                <span className="text-xs text-muted-foreground">Se desmarcado, o anúncio não será exibido no carrossel.</span>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-emerald-600"></div>
              </label>
            </div>

            <DialogFooter className="pt-4 border-t border-border flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setIsModalOpen(false)} className="rounded-xl font-bold text-xs">
                Cancelar
              </Button>
              <Button
                type="submit"
                disabled={createBannerMutation.isPending || updateBannerMutation.isPending}
                className="rounded-xl font-bold text-xs bg-primary hover:bg-primary-hover text-primary-foreground shadow-sm px-6"
              >
                {(createBannerMutation.isPending || updateBannerMutation.isPending) && (
                  <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
                )}
                <span>{editingBanner ? 'Salvar Alterações' : 'Criar e Publicar Anúncio'}</span>
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* MODAL DE CONFIGURAÇÕES GLOBAIS DO CARROSSEL DE ANÚNCIOS    */}
      {/* ========================================================= */}
      <Dialog open={isSettingsModalOpen} onOpenChange={(open) => !open && setIsSettingsModalOpen(false)}>
        <DialogContent className="max-w-md w-full p-6 rounded-3xl bg-card border border-border shadow-2xl space-y-4">
          <DialogHeader>
            <DialogTitle className="text-base font-bold text-foreground flex items-center gap-2">
              <Sliders className="w-5 h-5 text-primary" />
              <span>Configurações Globais do Carrossel</span>
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Ajuste as regras de transição automática dos slides para todos os usuários.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 text-xs">
            <div className="space-y-1">
              <label className="font-bold text-foreground">Tempo Padrão por Slide (Segundos)</label>
              <input
                type="number"
                min="3"
                max="30"
                value={globalSettings.defaultSlideDurationSeconds}
                onChange={(e) =>
                  setGlobalSettings({ ...globalSettings, defaultSlideDurationSeconds: parseInt(e.target.value, 10) || 6 })
                }
                className="w-full h-10 px-3 rounded-xl border border-input bg-background font-bold text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              />
              <p className="text-[11px] text-muted-foreground">Aplicado aos anúncios que não definirem um tempo específico.</p>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-border">
              <div>
                <span className="font-bold text-foreground block">Rotação Automática (Autoplay)</span>
                <span className="text-[11px] text-muted-foreground">Alterna os anúncios automaticamente no topo</span>
              </div>
              <input
                type="checkbox"
                checked={globalSettings.globalAutoplay}
                onChange={(e) => setGlobalSettings({ ...globalSettings, globalAutoplay: e.target.checked })}
                className="w-4 h-4 rounded text-primary"
              />
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-muted/30 border border-border">
              <div>
                <span className="font-bold text-foreground block">Permitir Dispensar na Sessão</span>
                <span className="text-[11px] text-muted-foreground">Usuário pode fechar o banner durante a navegação</span>
              </div>
              <input
                type="checkbox"
                checked={globalSettings.allowDismissForSession}
                onChange={(e) => setGlobalSettings({ ...globalSettings, allowDismissForSession: e.target.checked })}
                className="w-4 h-4 rounded text-primary"
              />
            </div>
          </div>

          <DialogFooter className="pt-2 flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsSettingsModalOpen(false)} className="rounded-xl text-xs font-bold">
              Cancelar
            </Button>
            <Button
              onClick={() => updateSettingsMutation.mutate(globalSettings)}
              disabled={updateSettingsMutation.isPending}
              className="rounded-xl text-xs font-bold bg-primary hover:bg-primary-hover text-primary-foreground"
            >
              {updateSettingsMutation.isPending && <Loader2 className="w-3 h-3 animate-spin mr-1.5" />}
              <span>Salvar Parâmetros</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========================================================= */}
      {/* MODAL DE CONFIRMAÇÃO DE EXCLUSÃO                          */}
      {/* ========================================================= */}
      {deleteConfirmId && (
        <Dialog open={true} onOpenChange={() => setDeleteConfirmId(null)}>
          <DialogContent className="max-w-md w-full p-6 rounded-3xl bg-card border border-border shadow-2xl space-y-4">
            <DialogHeader>
              <DialogTitle className="text-base font-bold text-destructive flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                <span>Confirmar Exclusão do Anúncio</span>
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Tem certeza que deseja excluir este anúncio? Ele deixará de ser exibido imediatamente no topo das telas.
              </DialogDescription>
            </DialogHeader>

            <DialogFooter className="pt-2 flex justify-end gap-2">
              <Button variant="outline" onClick={() => setDeleteConfirmId(null)} className="rounded-xl text-xs font-bold">
                Cancelar
              </Button>
              <Button
                onClick={() => deleteBannerMutation.mutate(deleteConfirmId)}
                disabled={deleteBannerMutation.isPending}
                className="rounded-xl text-xs font-bold bg-destructive hover:bg-destructive/90 text-white"
              >
                {deleteBannerMutation.isPending && <Loader2 className="w-3 h-3 animate-spin mr-1.5" />}
                <span>Confirmar Exclusão</span>
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
